import {type BoardRow, useBoardLabels, useBoardRowHeadingLevel} from '@github-ui/board-core'
import {VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'

import type {BoardHeadingLevel} from './BoardColumnHeader'
import styles from './BoardRowHeader.module.css'

export type BoardRowHeaderProps = {
  row: BoardRow
  /** Defaults to the level `<Board>` decides. Override when rendering a frame outside `<Board>`. */
  headingLevel?: BoardHeadingLevel
  /**
   * Drawn before the name: a colour swatch for a label or a status, an avatar for an
   * assignee. A sibling of the heading rather than content inside it, so a long name still
   * wraps against the swimlane's full width.
   *
   * Any flow content is allowed, so a visual built from a `<div>` needs no wrapper of its
   * own to be valid here.
   *
   * Decorative, and hidden from assistive technology: `aria-hidden` covers the whole
   * subtree, so an `alt` or an `aria-label` written inside it is never announced. Put
   * anything the visual conveys into the name beside it as well, and nothing focusable in
   * here -- a control a reader can tab to but is never told about belongs in `trailing`.
   */
  leadingVisual?: ReactNode
  /**
   * The name's content, in place of `row.label`. For a swimlane whose field has richer
   * material than a string -- a label's name carries emoji markup, for instance.
   *
   * It still renders inside the heading, so the level, the count and the wrapping stay this
   * component's. **Say the same thing `row.label` says**: that is what names every cell in
   * the swimlane, and a heading that disagrees with it names one swimlane twice.
   */
  children?: ReactNode
  /**
   * An overflow menu, or anything else that belongs beside the name.
   *
   * **A control here is a tab stop of its own**, which is the one thing that makes the
   * board more than a single stop. It also sits before the name in the reading order, so a
   * reader tabbing in from outside reaches every swimlane's control before the card they
   * left off at. That is the cost of putting a control here rather than a defect in it, and
   * it is worth knowing before a board grows twelve of them.
   */
  trailing?: ReactNode
  className?: string
}

/**
 * A swimlane's name and its count.
 *
 * As with a column, the count is `row.totalCount` and never a sum of the cells in the
 * row or a count of the cards it holds, and it is announced with the unit it is counting
 * rather than as a bare number.
 *
 * **The name is not a place the board's keyboard navigation stops.** It is not interactive,
 * and the cell a reader arrives in is already named after the swimlane it is in, so a stop
 * here would buy nothing they do not already hear. The count is drawn here for everybody
 * who can see it and is carried to everybody else in the swimlane's own accessible name,
 * which `useBoardRow` builds.
 */
export function BoardRowHeader({row, headingLevel, leadingVisual, children, trailing, className}: BoardRowHeaderProps) {
  const contextLevel = useBoardRowHeadingLevel()
  const Heading = `h${contextLevel ?? headingLevel ?? 2}` as const
  const labels = useBoardLabels()

  return (
    <div className={clsx(styles.RowHeader, className)}>
      {leadingVisual === undefined ? null : (
        <div aria-hidden="true" className={styles.LeadingVisual}>
          {leadingVisual}
        </div>
      )}
      <Heading className={styles.Name}>{children ?? row.label}</Heading>
      {row.totalCount === undefined ? null : (
        <span className={styles.Count}>
          {/* Nested for the same reason as the column's. See `BoardColumnHeader`. */}
          <span aria-hidden="true">{row.totalCount}</span>
          <VisuallyHidden>{labels.rowCount(row.totalCount)}</VisuallyHidden>
        </span>
      )}
      {trailing === undefined ? null : <span className={styles.Trailing}>{trailing}</span>}
    </div>
  )
}
